import { useEffect, useState, type DragEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CalendarRange,
  Clock,
  GripVertical,
  Inbox,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  cn,
  useToast,
  type BadgeTone,
} from '../../ui';
import { useSprintPlanning } from '../../features/sprintboard/useSprintPlanning';
import { sprintStatusLabel } from '../../features/sprintboard/models/sprintboardModel';
import { formatTaskEstimate } from '../../features/tasks/taskModel';
import { memberBandwidthDays } from '../../features/sprintboard/engine/capacity';
import type { Task } from '../../features/tasks/types';
import type { CapacitySummary } from '../../features/sprintboard/engine/capacity';
import type { CommonTask } from '../../features/sprintboard/models/types';
import type { TeamMember } from '../../features/teams/types';

const DND_MIME = 'text/plain';

/** A draggable planning card: type, title, estimate, owner. */
function PlanningCard({
  task,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  task: Task;
  dragging: boolean;
  onDragStart: (e: DragEvent<HTMLElement>, task: Task) => void;
  onDragEnd: () => void;
  onOpen: (task: Task) => void;
}) {
  const estimate = formatTaskEstimate(task);
  const estTone: BadgeTone =
    estimate === 'Not Estimated' ? 'neutral' : estimate === 'Spike' ? 'info' : 'accent';
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      className={cn(
        'group rounded-xl border border-surface-border bg-surface-1 p-3 shadow-sm transition-colors hover:border-zinc-600',
        dragging ? 'opacity-40' : 'opacity-100',
      )}
    >
      <div className="flex items-start gap-2">
        <GripVertical
          className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-zinc-600"
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => onOpen(task)}
          className="flex-1 text-left text-sm font-medium text-zinc-100 hover:text-white"
        >
          {task.title}
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
        <Badge tone="neutral">{task.type ?? 'Task'}</Badge>
        <Badge tone={estTone}>{estimate}</Badge>
        {task.blocked && (
          <Badge tone="danger" dot>
            Blocked
          </Badge>
        )}
        {task.owner && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500">
            <Users className="h-3 w-3" aria-hidden="true" />
            {task.owner}
          </span>
        )}
      </div>
    </div>
  );
}

/** A droppable pane (backlog or sprint) rendering a list of planning cards. */
function PlanningPane({
  title,
  icon,
  count,
  tasks,
  draggingId,
  isOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpen,
  emptyHint,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  tasks: Task[];
  draggingId: string | null;
  isOver: boolean;
  onDragStart: (e: DragEvent<HTMLElement>, task: Task) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
  onOpen: (task: Task) => void;
  emptyHint: string;
}) {
  return (
    <section
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex min-h-[16rem] flex-col rounded-2xl border bg-surface-2/50 p-3 transition-colors',
        isOver ? 'border-accent bg-surface-3/40' : 'border-surface-border',
      )}
    >
      <header className="mb-2 flex items-center gap-2 px-1">
        <span className="text-accent">{icon}</span>
        <span className="text-sm font-semibold text-zinc-100">{title}</span>
        <span className="rounded-full bg-surface-3 px-1.5 text-xs text-zinc-500">
          {count}
        </span>
      </header>
      <div className="flex flex-1 flex-col gap-2">
        {tasks.length === 0 ? (
          <p className="rounded-xl border border-dashed border-surface-border px-3 py-10 text-center text-xs text-zinc-600">
            {emptyHint}
          </p>
        ) : (
          tasks.map((t) => (
            <PlanningCard
              key={t.id}
              task={t}
              dragging={draggingId === t.id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </section>
  );
}

/** A labeled progress bar (percentage-filled), tinted danger when over. */
function CapacityBar({ pct, over }: { pct: number; over: boolean }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
      <div
        className={cn('h-full rounded-full', over ? 'bg-red-500' : 'bg-accent')}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

/** The sprint-level capacity header: team capacity / committed / remaining. */
function CapacityHeader({
  capacity,
  workingDays,
  hasTeam,
}: {
  capacity: CapacitySummary;
  workingDays: number;
  hasTeam: boolean;
}) {
  const { teamCapacityDays, committedDays, remainingDays, overCommitted } = capacity;
  const pct =
    teamCapacityDays > 0
      ? Math.round((committedDays / teamCapacityDays) * 100)
      : 0;

  return (
    <div className="mb-5 rounded-2xl border border-surface-border bg-surface-2 p-4">
      {hasTeam ? (
        <>
          <div className="mb-3 grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-zinc-500">Team capacity</p>
              <p className="text-lg font-semibold text-zinc-100">
                {teamCapacityDays}
                <span className="ml-1 text-xs font-normal text-zinc-500">days</span>
              </p>
              <p className="text-[11px] text-zinc-600">{workingDays} working days</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Committed</p>
              <p className="text-lg font-semibold text-zinc-100">
                {committedDays}
                <span className="ml-1 text-xs font-normal text-zinc-500">days</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Remaining</p>
              <p
                className={cn(
                  'text-lg font-semibold',
                  remainingDays < 0 ? 'text-red-300' : 'text-emerald-300',
                )}
              >
                {remainingDays}
                <span className="ml-1 text-xs font-normal text-zinc-500">days</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <CapacityBar pct={pct} over={overCommitted} />
            </div>
            <span
              className={cn(
                'text-sm font-medium',
                overCommitted ? 'text-red-300' : 'text-zinc-300',
              )}
            >
              {pct}%
            </span>
          </div>
          {overCommitted && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              Committed effort exceeds team capacity.
            </p>
          )}
          {capacity.unassignedDays > 0 && (
            <p className="mt-1 text-xs text-zinc-500">
              {capacity.unassignedDays} day{capacity.unassignedDays === 1 ? '' : 's'} of
              committed effort is unassigned or owned by a non-member.
            </p>
          )}
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm text-zinc-400">
          <Users className="h-4 w-4 text-zinc-500" aria-hidden="true" />
          Link a team to this sprint to see capacity. Committed effort so far:{' '}
          <span className="font-medium text-zinc-200">{committedDays} days</span>.
        </p>
      )}
    </div>
  );
}

/** Per-engineer assigned-vs-capacity bars with a non-blocking over-capacity warning. */
function PerEngineerCapacity({
  capacity,
  members,
}: {
  capacity: CapacitySummary;
  members: TeamMember[];
}) {
  if (members.length === 0) return null;
  const nameByAlias = new Map(members.map((m) => [m.alias, m.name]));

  return (
    <div className="mt-6 rounded-2xl border border-surface-border bg-surface-2 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-100">
        <Users className="h-4 w-4 text-accent" aria-hidden="true" />
        Per-engineer capacity
      </h2>
      <div className="space-y-3">
        {capacity.perEngineer.map((load) => {
          const pct =
            load.capacityDays > 0
              ? Math.round((load.assignedDays / load.capacityDays) * 100)
              : 0;
          const name = nameByAlias.get(load.alias) || load.alias;
          return (
            <div key={load.alias}>
              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-zinc-200">{name}</span>
                <span className="flex items-center gap-2">
                  <span className={cn(load.overCapacity ? 'text-red-300' : 'text-zinc-400')}>
                    {load.assignedDays} / {load.capacityDays} days
                  </span>
                  {load.overCapacity && (
                    <span className="inline-flex items-center gap-1 text-amber-300">
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                      Capacity exceeded
                    </span>
                  )}
                </span>
              </div>
              <CapacityBar pct={pct} over={load.overCapacity} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Bandwidth planning panel — the "Plan Sprint" surface for editing the two
 * inputs the capacity engine turns into each member's bandwidth:
 *   • per-member ON-CALL days (0..workingDays), with the resulting bandwidth
 *     shown live (working days − on-call, ×1.08, − common reduction), and
 *   • the sprint's COMMON / all-hands tasks (name + days-per-member), each of
 *     which reduces EVERY member's bandwidth. The total per-member reduction is
 *     shown alongside.
 */
function BandwidthPanel({
  members,
  workingDays,
  oncallDaysByAlias,
  commonTasks,
  commonReductionDays,
  saving,
  onSetOncall,
  onAddCommon,
  onRemoveCommon,
}: {
  members: TeamMember[];
  workingDays: number;
  oncallDaysByAlias: Record<string, number>;
  commonTasks: CommonTask[];
  commonReductionDays: number;
  saving: boolean;
  onSetOncall: (alias: string, days: number) => void;
  onAddCommon: (name: string, daysPerMember: number) => void;
  onRemoveCommon: (id: string) => void;
}) {
  // Local, editable mirror of on-call inputs so bandwidth updates live as typed.
  const [oncallInputs, setOncallInputs] = useState<Record<string, string>>({});
  const [commonName, setCommonName] = useState('');
  const [commonDays, setCommonDays] = useState('');

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const m of members) {
      const v = oncallDaysByAlias[m.alias];
      next[m.alias] = typeof v === 'number' && v > 0 ? String(v) : '';
    }
    setOncallInputs(next);
  }, [members, oncallDaysByAlias]);

  const parseNonNeg = (raw: string): number => {
    const n = Number(raw.trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const commitOncall = (alias: string) => {
    const clamped = Math.min(parseNonNeg(oncallInputs[alias] ?? ''), Math.max(0, workingDays));
    onSetOncall(alias, clamped);
  };

  const submitCommon = () => {
    const name = commonName.trim();
    if (!name) return;
    onAddCommon(name, parseNonNeg(commonDays));
    setCommonName('');
    setCommonDays('');
  };

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* On-call days per member → live bandwidth */}
      <section className="rounded-2xl border border-surface-border bg-surface-2 p-4">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <Clock className="h-4 w-4 text-accent" aria-hidden="true" />
          On-call days
        </h2>
        <p className="mb-3 text-xs text-zinc-500">
          Days each member is on call this sprint. Removed from their{' '}
          {workingDays} working days before the ×1.08 bandwidth multiplier.
        </p>
        {members.length === 0 ? (
          <p className="rounded-xl border border-dashed border-surface-border px-3 py-6 text-center text-xs text-zinc-600">
            Link a team to this sprint to set per-member on-call days.
          </p>
        ) : (
          <ul className="space-y-2">
            {members.map((m) => {
              const oncall = Math.min(
                parseNonNeg(oncallInputs[m.alias] ?? ''),
                Math.max(0, workingDays),
              );
              const bandwidth = memberBandwidthDays(
                workingDays,
                oncall,
                commonReductionDays,
              );
              return (
                <li
                  key={m.alias}
                  className="flex items-center gap-3 rounded-xl border border-surface-border bg-surface-1 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                    {m.name?.trim() || m.alias}
                    {m.role && (
                      <span className="ml-1 text-xs text-zinc-500">{m.role}</span>
                    )}
                  </span>
                  <label className="flex items-center gap-1.5 text-xs text-zinc-500">
                    on call
                    <input
                      type="number"
                      min={0}
                      max={workingDays}
                      step={0.5}
                      disabled={saving}
                      value={oncallInputs[m.alias] ?? ''}
                      onChange={(e) =>
                        setOncallInputs((prev) => ({
                          ...prev,
                          [m.alias]: e.target.value,
                        }))
                      }
                      onBlur={() => commitOncall(m.alias)}
                      className="h-8 w-16 rounded-lg border border-surface-border bg-surface-2 px-2 text-right text-sm text-zinc-100 focus:border-accent focus-visible:outline-none disabled:opacity-50"
                      aria-label={`On-call days for ${m.name?.trim() || m.alias}`}
                    />
                    d
                  </label>
                  <span
                    className="w-20 shrink-0 text-right text-xs tabular-nums text-accent-fg"
                    title="Resulting bandwidth this sprint"
                  >
                    {bandwidth}d
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Common / all-hands tasks → per-member reduction */}
      <section className="rounded-2xl border border-surface-border bg-surface-2 p-4">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <Activity className="h-4 w-4 text-accent" aria-hidden="true" />
          Common tasks
          <span className="ml-auto text-xs font-normal text-zinc-500">
            −
            <span className="tabular-nums text-amber-300">
              {commonReductionDays}d
            </span>{' '}
            per member
          </span>
        </h2>
        <p className="mb-3 text-xs text-zinc-500">
          All-hands work (ceremonies, all hands…) that reduces every member's
          bandwidth by its days-per-member.
        </p>
        {commonTasks.length > 0 && (
          <ul className="mb-3 space-y-1.5">
            {commonTasks.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 rounded-xl border border-surface-border bg-surface-1 px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                  {c.name}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-zinc-400">
                  {c.daysPerMember}d / member
                </span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => onRemoveCommon(c.id)}
                  title="Remove common task"
                  className="shrink-0 text-zinc-600 transition-colors hover:text-red-400 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Input
            label="Name"
            value={commonName}
            placeholder="Sprint Ceremonies"
            onChange={(e) => setCommonName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitCommon();
              }
            }}
          />
          <Input
            label="Days / member"
            type="number"
            min={0}
            step={0.5}
            value={commonDays}
            placeholder="0.5"
            className="sm:w-28"
            onChange={(e) => setCommonDays(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitCommon();
              }
            }}
          />
          <Button
            variant="secondary"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            loading={saving}
            onClick={submitCommon}
          >
            Add
          </Button>
        </div>
      </section>
    </div>
  );
}

const STATUS_TONE: Record<string, BadgeTone> = {
  planning: 'info',
  active: 'success',
  complete: 'neutral',
};

/**
 * Sprint Planning — a two-pane surface (room backlog on the left, the selected
 * sprint on the right) with native drag-and-drop in both directions to commit a
 * task to the sprint or return it to the backlog (which just sets/clears the
 * task's sprintId). A capacity header shows team capacity vs committed effort
 * with a progress bar, and a per-engineer breakdown flags anyone assigned
 * beyond their capacity — a warning only, never a block.
 */
export function SprintPlanningPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    sprint,
    backlog,
    sprintTasks,
    members,
    capacity,
    workingDays,
    commonTasks,
    oncallDaysByAlias,
    commonReductionDays,
    saving,
    loading,
    error,
    assign,
    setOncallDays,
    addCommonTask,
    removeCommonTask,
  } = useSprintPlanning(id);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overPane, setOverPane] = useState<'backlog' | 'sprint' | null>(null);

  const onDragStart = (e: DragEvent<HTMLElement>, task: Task) => {
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND_MIME, task.id);
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setOverPane(null);
  };

  const paneDragOver = (pane: 'backlog' | 'sprint') => (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overPane !== pane) setOverPane(pane);
  };
  const paneDragLeave = (pane: 'backlog' | 'sprint') => (e: DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setOverPane((p) => (p === pane ? null : p));
    }
  };

  const handleDrop =
    (target: 'backlog' | 'sprint') => (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData(DND_MIME) || draggingId;
      setOverPane(null);
      setDraggingId(null);
      if (!taskId || !sprint) return;
      const task = [...backlog, ...sprintTasks].find((t) => t.id === taskId);
      if (!task) return;
      const targetSprintId = target === 'sprint' ? sprint.id : null;
      if ((task.sprintId ?? null) === targetSprintId) return; // already there
      void assign(task, targetSprintId).catch((err) =>
        toast({
          tone: 'error',
          title: 'Move failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
      );
    };

  const openTask = (task: Task) => navigate(`/tasks/${task.id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading sprint" />
      </div>
    );
  }

  if (error || !sprint) {
    return (
      <div>
        <PageHeader
          icon={<CalendarRange className="h-5 w-5" />}
          title="Sprint planning"
        />
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Sprint not found"
          description={error ?? 'This sprint does not exist or was removed.'}
          action={
            <Button
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/work/sprints')}
            >
              Back to Sprints
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={<CalendarRange className="h-5 w-5" />}
        title={sprint.name}
        description={
          <span className="inline-flex items-center gap-2">
            <Badge tone={STATUS_TONE[sprint.status] ?? 'neutral'}>
              {sprintStatusLabel(sprint.status)}
            </Badge>
            {sprint.goal && <span className="text-zinc-500">{sprint.goal}</span>}
          </span>
        }
        actions={
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/work/sprints')}
          >
            All sprints
          </Button>
        }
      />

      <CapacityHeader
        capacity={capacity}
        workingDays={workingDays}
        hasTeam={members.length > 0}
      />

      <BandwidthPanel
        members={members}
        workingDays={workingDays}
        oncallDaysByAlias={oncallDaysByAlias}
        commonTasks={commonTasks}
        commonReductionDays={commonReductionDays}
        saving={saving}
        onSetOncall={(alias, days) =>
          void setOncallDays(alias, days).catch((e) =>
            toast({
              tone: 'error',
              title: 'Couldn’t save on-call days',
              description: e instanceof Error ? e.message : 'Unknown error',
            }),
          )
        }
        onAddCommon={(name, days) =>
          void addCommonTask(name, days).catch((e) =>
            toast({
              tone: 'error',
              title: 'Couldn’t add common task',
              description: e instanceof Error ? e.message : 'Unknown error',
            }),
          )
        }
        onRemoveCommon={(cid) =>
          void removeCommonTask(cid).catch((e) =>
            toast({
              tone: 'error',
              title: 'Couldn’t remove common task',
              description: e instanceof Error ? e.message : 'Unknown error',
            }),
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PlanningPane
          title="Backlog"
          icon={<Inbox className="h-4 w-4" />}
          count={backlog.length}
          tasks={backlog}
          draggingId={draggingId}
          isOver={overPane === 'backlog'}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={paneDragOver('backlog')}
          onDragLeave={paneDragLeave('backlog')}
          onDrop={handleDrop('backlog')}
          onOpen={openTask}
          emptyHint="Drag tasks here to return them to the backlog."
        />
        <PlanningPane
          title={`Sprint · ${sprint.name}`}
          icon={<CalendarRange className="h-4 w-4" />}
          count={sprintTasks.length}
          tasks={sprintTasks}
          draggingId={draggingId}
          isOver={overPane === 'sprint'}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={paneDragOver('sprint')}
          onDragLeave={paneDragLeave('sprint')}
          onDrop={handleDrop('sprint')}
          onOpen={openTask}
          emptyHint="Drag tasks from the backlog to commit them to this sprint."
        />
      </div>

      <PerEngineerCapacity capacity={capacity} members={members} />
    </div>
  );
}

export default SprintPlanningPage;
